import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { User } from '../../shared/models/user.model';

export interface UserProfile {
  id: number;
  name: string;
  first_name: string;
  last_name: string;
  email: string;
  email_verified_at: string | null;
  socialAccounts: any[];
  hasSubscriptions: boolean;
  pending_activation: boolean;
  current_subscription: any | null;
  business_name: string | null;
  phone: string | null;
  website: string | null;
  token?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthFlowService {
  constructor(
    private router: Router,
    private authService: AuthService
  ) {}

  /**
   * Redirect user based on their current authentication state
   * @param userProfile Optional user profile. If not provided, will use cached user from localStorage
   */
  redirectToAppropriateRoute(userProfile?: UserProfile): void {
    if (userProfile) {
      this.handleRedirect(userProfile);
    } else {
      // Try to get cached user from localStorage first to avoid infinite API calls
      const cachedUser = localStorage.getItem('user');
      if (cachedUser) {
        try {
          const profile = JSON.parse(cachedUser);
          this.handleRedirect(profile);
        } catch (error) {
          console.error('Error parsing cached user:', error);
          // If cache is invalid, redirect to login
          this.router.navigate(['/auth/login']);
        }
      } else {
        // No cached user, redirect to login
        this.router.navigate(['/auth/login']);
      }
    }
  }

  /**
   * Handle redirect logic based on user state
   */
  private handleRedirect(profile: UserProfile): void {

    // Ensure user is saved to localStorage before navigation (for guards to access)
    localStorage.setItem('user', JSON.stringify(profile));
    // Also update the auth service current user subject
    // Cast to User type since the profile from API should match User interface
    this.authService.currentUserSubject.next(profile as User);

    // Use setTimeout to ensure localStorage is saved and guards can read it
    // This is especially important on server environments where timing can differ
    setTimeout(() => {
      this.performNavigation(profile);
    }, 100);
  }

  /**
   * Perform the actual navigation based on user profile
   */
  private performNavigation(profile: UserProfile): void {
    // 1. Check if email is not verified
    if (!profile.email_verified_at) {
      this.router.navigate(['/auth/email-verification'], {
        queryParams: { email: profile.email },
        replaceUrl: true
      })
      return;
    }

    // 2. Check if user has no subscription
    if (!profile.hasSubscriptions) {
      this.router.navigate(['/auth/choose-plan'], { replaceUrl: true })
      return;
    }

    // 3. Check if account setup is not completed (no social accounts)
    if (!profile.socialAccounts || profile.socialAccounts.length === 0) {
      this.router.navigate(['/auth/account-setup'], { replaceUrl: true });
      return;
    }

    // 4. Check if social account is missing required fields (child_business_manager, child_business_manager_ad_account, instagram_account)
    // This check MUST happen before pending_activation check
    if (profile.socialAccounts && profile.socialAccounts.length > 0) {
      const firstSocialAccount = profile.socialAccounts[0];
      const missingFields = [];
      
      // Explicitly check for null, undefined, or empty object
      if (!firstSocialAccount.child_business_manager || 
          firstSocialAccount.child_business_manager === null ||
          (typeof firstSocialAccount.child_business_manager === 'object' && Object.keys(firstSocialAccount.child_business_manager).length === 0)) {
        missingFields.push('child_business_manager');
      }
      if (!firstSocialAccount.child_business_manager_ad_account || 
          firstSocialAccount.child_business_manager_ad_account === null ||
          (typeof firstSocialAccount.child_business_manager_ad_account === 'object' && Object.keys(firstSocialAccount.child_business_manager_ad_account).length === 0)) {
        missingFields.push('child_business_manager_ad_account');
      }
      if (!firstSocialAccount.instagram_account || 
          firstSocialAccount.instagram_account === null ||
          (typeof firstSocialAccount.instagram_account === 'object' && Object.keys(firstSocialAccount.instagram_account).length === 0)) {
        missingFields.push('instagram_account');
      }
      
      if (missingFields.length > 0) {
        this.router.navigate(['/auth/choose-instagram'], { replaceUrl: true })
        return;
      }
      
      // If social account is complete and pending activation, redirect to follower-growth-ads
      // This starts the ad setup flow
      if (profile.pending_activation) {
        this.router.navigate(['/auth/follower-growth-ads'], { replaceUrl: true })
        return;
      }
    }

    // 5. Fallback: If pending activation but no social accounts (shouldn't happen)
    if (profile.pending_activation) {
      this.router.navigate(['/auth/account-activation'], { replaceUrl: true })
      return;
    }

    // 6. All steps completed - redirect to dashboard
    this.router.navigate(['/dashboard'], { replaceUrl: true })
  }

  /**
   * Get the next step in the auth flow for a given user
   */
  getNextStep(profile: UserProfile): string {
    if (!profile.email_verified_at) {
      return '/auth/email-verification';
    }

    if (!profile.hasSubscriptions) {
      return '/auth/choose-plan';
    }

    if (!profile.socialAccounts || profile.socialAccounts.length === 0) {
      return '/auth/account-setup';
    }

    // Check if social account is missing required fields
    // This check MUST happen before pending_activation check
    if (profile.socialAccounts && profile.socialAccounts.length > 0) {
      const firstSocialAccount = profile.socialAccounts[0];
      // Explicitly check for null, undefined, or empty object
      const hasChildBusinessManager = firstSocialAccount.child_business_manager && 
                                      firstSocialAccount.child_business_manager !== null &&
                                      !(typeof firstSocialAccount.child_business_manager === 'object' && Object.keys(firstSocialAccount.child_business_manager).length === 0);
      const hasChildBusinessManagerAdAccount = firstSocialAccount.child_business_manager_ad_account && 
                                               firstSocialAccount.child_business_manager_ad_account !== null &&
                                               !(typeof firstSocialAccount.child_business_manager_ad_account === 'object' && Object.keys(firstSocialAccount.child_business_manager_ad_account).length === 0);
      const hasInstagramAccount = firstSocialAccount.instagram_account && 
                                  firstSocialAccount.instagram_account !== null &&
                                  !(typeof firstSocialAccount.instagram_account === 'object' && Object.keys(firstSocialAccount.instagram_account).length === 0);
      
      if (!hasChildBusinessManager || !hasChildBusinessManagerAdAccount || !hasInstagramAccount) {
        return '/auth/choose-instagram';
      }
      
      // If social account is complete and pending_activation, redirect to account-activation
      // Users accessing completed routes should go straight to activation, not ad setup flow
      if (profile.pending_activation) {
        return '/auth/account-activation';
      }
    }

    // If pending_activation but no social accounts (shouldn't happen, but fallback)
    if (profile.pending_activation) {
      return '/auth/account-activation';
    }

    return '/dashboard';
  }

  /**
   * Check if user can access a specific route based on their profile
   */
  canAccessRoute(route: string, profile: UserProfile): boolean {
    const nextStep = this.getNextStep(profile);
    
    // User can access their next step or any previous step
    const allowedRoutes = this.getAllowedRoutes(profile);
    return allowedRoutes.includes(route);
  }

  /**
   * Check if the user's social account is complete (has all required fields)
   */
  isSocialAccountComplete(profile: UserProfile): boolean {
    if (!profile.socialAccounts || profile.socialAccounts.length === 0) {
      return false;
    }

    const firstSocialAccount = profile.socialAccounts[0];
    
    const hasChildBusinessManager = firstSocialAccount.child_business_manager && 
                                   firstSocialAccount.child_business_manager !== null &&
                                   !(typeof firstSocialAccount.child_business_manager === 'object' && Object.keys(firstSocialAccount.child_business_manager).length === 0);
    
    const hasChildBusinessManagerAdAccount = firstSocialAccount.child_business_manager_ad_account && 
                                            firstSocialAccount.child_business_manager_ad_account !== null &&
                                            !(typeof firstSocialAccount.child_business_manager_ad_account === 'object' && Object.keys(firstSocialAccount.child_business_manager_ad_account).length === 0);
    
    const hasInstagramAccount = firstSocialAccount.instagram_account && 
                               firstSocialAccount.instagram_account !== null &&
                               !(typeof firstSocialAccount.instagram_account === 'object' && Object.keys(firstSocialAccount.instagram_account).length === 0);
    
    return hasChildBusinessManager && hasChildBusinessManagerAdAccount && hasInstagramAccount;
  }

  /**
   * Check if user has completed all onboarding steps but is pending activation
   */
  isAwaitingActivation(profile: UserProfile): boolean {
    return !!(
      profile.email_verified_at &&
      profile.hasSubscriptions &&
      profile.socialAccounts &&
      profile.socialAccounts.length > 0 &&
      this.isSocialAccountComplete(profile) &&
      profile.pending_activation
    );
  }

  /**
   * Get all routes the user is allowed to access based on their current state
   * Flow order: email-verification -> choose-plan -> stripe-checkout -> account-setup -> choose-instagram -> account-activation -> dashboard
   */
  getAllowedRoutes(profile: UserProfile): string[] {
    const routes: string[] = ['/auth/login', '/auth/register'];

    // Email verification - always accessible if not verified
    if (profile.email_verified_at) {
      routes.push('/auth/email-verification');
    }

    // Choose-plan - accessible if email verified OR if user has subscriptions (to allow plan changes)
    if (profile.email_verified_at || profile.hasSubscriptions) {
      routes.push('/auth/choose-plan');
    }

    // Stripe checkout - accessible if email verified (for initial subscription) OR if user has subscriptions (for plan changes/upgrades)
    if (profile.email_verified_at || profile.hasSubscriptions) {
      routes.push('/auth/stripe-checkout');
    }

    // Account setup - only accessible after subscription
    if (profile.hasSubscriptions) {
      routes.push('/auth/account-setup');
    }

    // Choose Instagram - only accessible after account setup (has social accounts)
    if (profile.hasSubscriptions && profile.socialAccounts && profile.socialAccounts.length > 0) {
      routes.push('/auth/choose-instagram');
    }

    // Post-subscription routes - require subscription AND complete social account setup
    if (profile.hasSubscriptions && this.isSocialAccountComplete(profile)) {
      routes.push('/auth/follower-growth-ads', '/auth/dm-ads', '/auth/lead-ads');
    }

    // Account activation - requires subscription
    if (profile.hasSubscriptions && profile.pending_activation) {
      routes.push('/auth/account-activation');
    }

    // Dashboard - requires subscription, social accounts, and complete setup
    // Allow access even with pending_activation (users can stay on dashboard while waiting)
    if (profile.hasSubscriptions && this.isSocialAccountComplete(profile)) {
      routes.push('/dashboard', '/');
    }

    return routes;
  }
}
